import React from "react";
import { Icon } from "../icons/icon";

interface PaginationProps {
  currentPage: number;
  totalCount: number;
  itemsPerPage: number;
  pageRangeDisplayed?: number;
  onPageChange: (page: number) => void;
  className?: string;
  containerClassName?: string;
  showPagNumbers?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalCount,
  itemsPerPage,
  pageRangeDisplayed = 3,
  onPageChange,
  className = "",
  containerClassName = "",
  showPagNumbers = true,
}) => {
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const getPageNumbers = (): any => {
    if (totalPages > pageRangeDisplayed) {
      const startPage = Math.max(2, currentPage - 2);
      const endPage = Math.min(totalPages - 1, currentPage + 2);

      let pages = [];
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (startPage > 2) {
        pages = ["...", ...pages];
      }
      if (endPage < totalPages - 1) {
        pages = [...pages, "..."];
      }

      return [1, ...pages, totalPages];
    }

    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const handlePageChange = (page: number): void => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const buttonClassName = `
  py-1 flex items-center justify-center gap-2
  soft-shrink rounded-[4px]
  min-w-[21px] h-[24px]
  text-[#454545] font-medium w-fit
  `;

  return totalPages < 1 ? undefined : (
    <nav
      className={`flex flex-wrap md:flex-nowrap w-full items-center justify-between gap-4 px-3 py-2 text-xs ${containerClassName}`}
    >
      <div className="text-xs font-bold text-grey-900">
        <span>
          Showing <b>{(currentPage - 1) * itemsPerPage + 1}</b> -{" "}
          <b>
            {currentPage * itemsPerPage > totalCount
              ? totalCount
              : currentPage * itemsPerPage}
          </b>{" "}
          of <b>{totalCount}</b>
        </span>
      </div>

      <ul
        className={`overflow-y-visible overflow-x-scroll min-w-max flex flex-1 list-none flex-nowrap items-center justify-end gap-2 ${className}`}
      >
        <li
          className={` ${
            currentPage === 1 ? "cursor-not-allowed opacity-50" : ""
          }`}
        >
          <button
            type="button"
            onClick={() => {
              handlePageChange(currentPage - 1);
            }}
            className={`${buttonClassName} !p-1 bg-light-green-bg-300 text-pry-dark-green-300`}
            disabled={currentPage === 1}
          >
            <Icon
              svg="caret-down"
              height={16}
              width={16}
              className="rotate-90"
            />
          </button>
        </li>

        {showPagNumbers &&
          getPageNumbers().map((number: number, index: number) => (
            <li key={index} className="overflow block !min-w-fit">
              {typeof number === "string" ? (
                <span className={buttonClassName}>{number}</span>
              ) : (
                <button
                  onClick={() => {
                    handlePageChange(number);
                  }}
                  className={`
                    ${buttonClassName} 
                    bg-white border border-neutral-200
                    ${
                      number === currentPage &&
                      "!bg-pry-green-200 !border-pry-green-200 !text-white"
                    }`}
                >
                  {number}
                </button>
              )}
            </li>
          ))}

        <li
          className={`${
            currentPage === totalPages ? "cursor-not-allowed opacity-50" : ""
          }`}
        >
          <button
            type="button"
            onClick={() => {
              handlePageChange(currentPage + 1);
            }}
            className={`${buttonClassName} !p-1 bg-light-green-bg-300 text-pry-dark-green-300`}
          >
            <Icon
              svg="caret-down"
              height={16}
              width={16}
              className="-rotate-90"
            />
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default Pagination;
